/**
 * Turns `data-mp-name` annotations into Mixpanel event names.
 *
 * Mixpanel autocapture cannot rename `[Auto] Element Click`, and it only reads
 * extra attrs off the exact click target — which Vuetify often misses because
 * the click lands on an inner span/icon. This listener walks ancestors with
 * `closest('[data-mp-name]')` and fires a real `mixpanel.track()` instead.
 *
 * Opt-outs:
 *   data-mp-ignore  — do not track (decorative / internal)
 *   data-mp-manual  — a click handler already fires a Mixpanel event; skip to
 *                     avoid duplicates (used for historical funnel names)
 */

const SKIP_ATTRS = new Set(['data-mp-name', 'data-mp-ignore', 'data-mp-manual'])
const SKIP_CHANGE_INPUT_TYPES = new Set([
    'checkbox', 'radio', 'button', 'submit', 'reset', 'file', 'hidden',
])

let started = false

function titleCaseToken(token) {
    if (!token) return ''
    return token.charAt(0).toUpperCase() + token.slice(1)
}

export function toClickEventName(mpName, eventType = 'click') {
    const body = String(mpName || '')
        .split('.')
        .filter(Boolean)
        .map(part => part.split('-').filter(Boolean).map(titleCaseToken).join(' '))
        .join(' - ')
    const suffix = eventType === 'change' ? 'Changed' : 'Clicked'
    return body ? `${body} - ${suffix}` : suffix
}

function attrKey(attrName) {
    const key = attrName.slice('data-mp-'.length).replace(/-([a-z])/g, (_, c) => c.toUpperCase())
    return key === 'index' ? 'mpIndex' : key
}

export function extraPropsFromEl(el) {
    const props = { mpName: el.getAttribute('data-mp-name') }
    const attrs = el.attributes
    if (!attrs) {
        const index = el.getAttribute('data-mp-index')
        if (index) props.mpIndex = index
        return props
    }
    for (const attr of attrs) {
        if (!attr.name.startsWith('data-mp-') || SKIP_ATTRS.has(attr.name) || !attr.value) continue
        props[attrKey(attr.name)] = attr.value
    }
    return props
}

export function findTaggedEl(node) {
    let el = node
    if (el && el.nodeType === 3) el = el.parentElement
    if (!el || typeof el.closest !== 'function') return null
    if (el.closest('[data-mp-ignore]')) return null
    const tagged = el.closest('[data-mp-name]')
    if (!tagged) return null
    if (tagged.hasAttribute('data-mp-ignore') || tagged.hasAttribute('data-mp-manual')) return null
    return tagged
}

export function shouldTrackDomEvent(ev) {
    if (!ev) return false
    if (ev.type === 'click') return ev.button == null || ev.button === 0
    if (ev.type !== 'change') return false
    const target = ev.target
    if (!target || !target.tagName) return false
    const tag = target.tagName.toLowerCase()
    if (tag === 'select' || tag === 'textarea') return true
    if (tag !== 'input') return false
    const type = (target.type || 'text').toLowerCase()
    return !SKIP_CHANGE_INPUT_TYPES.has(type)
}

function handleEvent(ev, mixpanel) {
    if (!shouldTrackDomEvent(ev)) return
    const el = findTaggedEl(ev.target)
    if (!el) return
    const mpName = el.getAttribute('data-mp-name')
    if (!mpName || typeof mixpanel?.track !== 'function') return
    mixpanel.track(toClickEventName(mpName, ev.type), extraPropsFromEl(el))
}

export function initMixpanelClicks({ mixpanel, root } = {}) {
    if (started) return
    const mp = mixpanel || (typeof window !== 'undefined' ? window.mixpanel : null)
    const target = root || (typeof document !== 'undefined' ? document : null)
    if (!mp || !target?.addEventListener) return
    started = true
    const listener = ev => handleEvent(ev, mp)
    target.addEventListener('click', listener, true)
    target.addEventListener('change', listener, true)
}

export function _resetMixpanelClicksForTests() {
    started = false
}
